import React, { useState, useEffect } from "react";
import axios from "axios";
import Select from "react-select";
import colors from "../Constants/colors";

const Result = () => {
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState("");
  const [sections, setSections] = useState([]);
  const [selectedSection, setSelectedSection] = useState("");
  const [exams, setExams] = useState([]);
  const [selectedExam, setSelectedExam] = useState("");
  const [students, setStudents] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [subjectMarks, setSubjectMarks] = useState({});
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [scores, setScores] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [editingResultId, setEditingResultId] = useState("");
  const [results, setResults] = useState([]);
  const [teacher, setTeacher] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const labelStyle = "text-black font-semibold mb-1 !text-[18px]";
  const inputStyle =
    "border rounded-lg px-3 py-2 outline-none !text-[14px] placeholder:!text-[14px] focus:ring-2 focus:ring-blue-500";

useEffect(() => {
  // Get teacher info and sessionId from localStorage outside the async function
  const teacherRaw = localStorage.getItem("teacher");
  const sessionId = localStorage.getItem("currentSessionId");

  if (!teacherRaw || !sessionId) {
    console.error("Missing teacher or sessionId");
    return;
  }

  const teacherData = JSON.parse(teacherRaw);
  const teacherId = teacherData.id;

  if (!teacherId) {
    console.error("Teacher ID not found");
    return;
  }

  const fetchTeacher = async () => {
    try {
      // Fetch teacher data
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/teachers/getTeacherById/${teacherId}`,
        { params: { sessionId } } // only sessionId as query param
      );

      setTeacher(response.data);

      // Fetch classes using teacher's full name
      const { firstName, lastName } = response.data;
      fetchClasses(`${firstName} ${lastName}`);
    } catch (err) {
      console.error("Error fetching teacher data:", err);
    }
  };

  fetchTeacher();
}, []);

const sessionId = localStorage.getItem("currentSessionId");
  const fetchClasses = async (teacherName) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/timetable/getTeacherClasses/${teacherName}`,
        { params: { sessionId } }
      );
      setClasses(response.data);
    } catch (error) {
      console.error("Error fetching classes:", error);
    }
  };

  const fetchSubjects = async (className) => {
    if (!teacher) return;
    try {
      const { firstName, lastName } = teacher;
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/timetable/getSubjectsByTeacherAndClass/${firstName}%20${lastName}/${className}`,
        { params: { sessionId } }
      );
      if (Array.isArray(response.data.subjects)) {
        setSubjects(response.data.subjects);
      } else {
        setSubjects([]);
      }
    } catch (error) {
      console.error("Error fetching subjects:", error);
    }
  };

  useEffect(() => {
    if (selectedClass) {
      axios
        .get(
          `${process.env.REACT_APP_API_BASE_URL}/api/sections/${selectedClass}/getSectionsByClassName`,
          { params: { sessionId } }
        )
        .then((response) => setSections(response.data))
        .catch((error) =>
          console.error("Error fetching sections:", error)
        );

      axios
        .get(
          `${process.env.REACT_APP_API_BASE_URL}/api/exams/getExamByClass/${selectedClass}`,
          { params: { sessionId } }
        )
        .then((response) => setExams(response.data))
        .catch((error) => console.error("Error fetching exams:", error));

      fetchSubjects(selectedClass);
    }
  }, [selectedClass, teacher]);

  useEffect(() => {
    if (selectedClass && selectedSection) {
      axios
        .get(`${process.env.REACT_APP_API_BASE_URL}/api/students/getStudents`, { params: { sessionId } })
        .then((response) => {
          const filtered = response.data.filter(
            (s) =>
              s.class === selectedClass &&
              s.section === selectedSection &&
              s.status === "active"
          );
          setStudents(filtered);
        })
        .catch((error) =>
          console.error("Error fetching students:", error)
        );
    }
  }, [selectedClass, selectedSection]);

  const handleSubjectMarksChange = (subjectName, value) => {
    setSubjectMarks((prev) => ({ ...prev, [subjectName]: value }));
  };

  const handleScoreChange = (studentId, subjectName, score) => {
    setScores((prev) => ({
      ...prev,
      [studentId]: { ...prev[studentId], [subjectName]: score },
    }));
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    const data = {
      examName: selectedExam,
      className: selectedClass,
      sectionName: selectedSection,
      subjects: selectedSubjects.map((s) => ({
        subjectName: s.value,
        totalMarks: subjectMarks[s.value] || 0,
      })),
      students: students.map((stu) => ({
        studentId: stu._id,
        rollNo: stu.rollNo,
        studentName: `${stu.firstName} ${stu.lastName}`,
        scores: selectedSubjects.map((s) => ({
          subjectName: s.value,
          score: scores[stu._id]?.[s.value] || 0,
        })),
      })),
    };

    try {
      if (isEditing) {
        await axios.put(
          `${process.env.REACT_APP_API_BASE_URL}/api/subjectResults/update/${editingResultId}`,
          data
        );
        alert("Result updated successfully!");
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_BASE_URL}/api/subjectResults/create`,
          data
        );
        alert("Result saved successfully!");
      }
      fetchResults();
      clearForm();
    } catch (error) {
      console.error("Error saving result:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchResults = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/subjectResults`
      );
      setResults(response.data);
    } catch (error) {
      console.error("Error fetching results:", error);
    }
  };

  const handleEdit = (r) => {
    setSelectedClass(r.className);
    setSelectedSection(r.sectionName);
    setSelectedExam(r.examName);

    setSubjectMarks(
      r.subjects.reduce(
        (a, s) => ({ ...a, [s.subjectName]: s.totalMarks }),
        {}
      )
    );

    setScores(
      r.students.reduce(
        (a, s) => ({
          ...a,
          [s.studentId]: s.scores.reduce(
            (b, sc) => ({ ...b, [sc.subjectName]: sc.score }),
            {}
          ),
        }),
        {}
      )
    );

    // For react-select selectedSubjects
    const mappedSelected = r.subjects.map((s) => ({
      value: s.subjectName,
      label: s.subjectName,
    }));
    setSelectedSubjects(mappedSelected);

    setEditingResultId(r._id);
    setIsEditing(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this result?")) return;
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/api/subjectResults/delete/${id}`
      );
      alert("Result deleted!");
      fetchResults();
    } catch (error) {
      console.error("Error deleting:", error);
    }
  };

  const clearForm = () => {
    setSelectedClass("");
    setSelectedSection("");
    setSelectedExam("");
    setSelectedSubjects([]);
    setSubjectMarks({});
    setScores({});
    setIsEditing(false);
  };

  useEffect(() => {
    fetchResults();
  }, []);

  const handleInputChange = (e) => {
    const [cls, sec] = e.target.value.split("|");
    setSelectedClass(cls);
    setSelectedSection(sec);
  };
const getFilteredResults = () => {
  // 🚫 Do not show anything until class & exam are selected
  if (!selectedClass || !selectedExam) return [];

  return results.filter(
    (r) =>
      r.className === selectedClass &&
      r.examName === selectedExam
  );
};

  return (
    <div className="container mx-auto p-6 bg-white shadow-lg rounded-lg">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <i className="fas fa-graduation-cap text-blue-600 !text-[20px]" />
          <h1 className="font-bold !text-[18px]">Exam Results</h1>
        </div>
        {isEditing && (
          <span className="text-sm text-gray-500">
            Editing result –{" "}
            <span className="font-semibold">Update mode</span>
          </span>
        )}
      </div>
      <p className="text-gray-600 mb-6 !text-[14px]">
        Manage class exams, subjects, and student scores.
      </p>

      {/* Result Entry Card */}
      <div className="bg-white shadow-md rounded-lg p-6 border border-gray-200 mb-8">
        <div className="flex items-center mb-4 gap-2">
          <i className="fas fa-file-alt text-blue-600 !text-[18px]" />
          <h2 className="font-semibold !text-[18px] text-gray-800">
            {isEditing ? "Edit Exam Result" : "Add New Exam Result"}
          </h2>
        </div>

        {/* Class & Exam */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex flex-col">
            <label className={labelStyle}>Class:</label>
            <select
              name="className"
              value={
                selectedClass && selectedSection
                  ? `${selectedClass}|${selectedSection}`
                  : ""
              }
              onChange={handleInputChange}
              className={inputStyle}
            >
              <option value="">Select Class</option>
              {classes.map((cls) => (
                <option
                  key={cls._id}
                  value={`${cls.className}|${cls.sectionName}`}
                >
                  {cls.className} ({cls.sectionName})
                </option>
              ))}
            </select>
          </div>

          <div className="flex flex-col">
            <label className={labelStyle}>Exam Name:</label>
            <select
              value={selectedExam}
              onChange={(e) => setSelectedExam(e.target.value)}
              className={inputStyle}
            >
              <option value="">Select Exam</option>
              {exams.map((exam) => (
                <option key={exam._id} value={exam.examName}>
                  {exam.examName}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Subjects Multi-select */}
        <div className="mt-6">
          <label className={labelStyle}>Subjects:</label>
          <div className="border rounded-lg px-2 py-1">
            <Select
              isMulti
              options={subjects.map((s) => ({ value: s, label: s }))}
              value={selectedSubjects}
              onChange={setSelectedSubjects}
              classNamePrefix="react-select"
            />
          </div>
        </div>

        {/* Subject Marks Table */}
        {selectedSubjects.length > 0 && (
          <div className="mt-6 overflow-x-auto">
            <h3 className="font-semibold !text-[16px] mb-2 text-gray-800 flex items-center gap-2">
              <i className="fas fa-clipboard-list text-blue-600" />
              Subject Marks
            </h3>
            <table className="min-w-full border border-gray-300 text-center !text-[14px] bg-white">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="border px-4 py-2 font-semibold">
                    Subject
                  </th>
                  <th className="border px-4 py-2 font-semibold">
                    Total Marks
                  </th>
                </tr>
              </thead>
              <tbody>
                {selectedSubjects.map((sub) => (
                  <tr key={sub.value} className="border-b">
                    <td className="border px-4 py-2">{sub.label}</td>
                    <td className="border px-4 py-2">
                      <input
                        type="number"
                        value={subjectMarks[sub.value] || ""}
                        onChange={(e) =>
                          handleSubjectMarksChange(
                            sub.value,
                            e.target.value
                          )
                        }
                        className={`${inputStyle} !px-2 !py-1`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Student Scores Table */}
        {students.length > 0 && selectedSubjects.length > 0 && (
          <div className="mt-8 overflow-x-auto">
            <h3 className="font-semibold !text-[16px] mb-2 text-gray-800 flex items-center gap-2">
              <i className="fas fa-user-graduate text-green-600" />
              Student Scores
            </h3>
            <table className="min-w-full border border-gray-300 text-center !text-[14px] bg-white">
              <thead className="bg-gray-100 text-gray-800">
                <tr>
                  <th className="border px-3 py-2 font-semibold">#</th>
                  <th className="border px-3 py-2 font-semibold">
                    Roll No
                  </th>
                  <th className="border px-3 py-2 font-semibold">
                    Student
                  </th>
                  {selectedSubjects.map((sub) => (
                    <th
                      key={sub.value}
                      className="border px-3 py-2 font-semibold"
                    >
                      {sub.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {students.map((stu, i) => (
                  <tr key={stu._id} className="border-b">
                    <td className="border px-3 py-2">{i + 1}</td>
                    <td className="border px-3 py-2">{stu.rollNo}</td>
                    <td className="border px-3 py-2">
                      {stu.firstName} {stu.lastName}
                    </td>
                    {selectedSubjects.map((sub) => (
                      <td
                        key={sub.value}
                        className="border px-3 py-2"
                      >
                        <input
                          type="number"
                          value={
                            scores[stu._id]?.[sub.value] || ""
                          }
                          onChange={(e) =>
                            handleScoreChange(
                              stu._id,
                              sub.value,
                              e.target.value
                            )
                          }
                          className={`${inputStyle} !px-2 !py-1`}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Buttons */}
        <div className="flex justify-end gap-4 mt-6">
          {isEditing && (
            <button
              onClick={clearForm}
              className="bg-red-600 hover:bg-red-500 text-white px-4 py-2 rounded-lg !text-[14px]"
            >
              Cancel
            </button>
          )}
          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className="text-white px-4 py-2 rounded-lg !text-[14px] hover:opacity-90"
            style={{ background: colors.gradientPrimary }}
          >
            {isLoading
              ? "Saving..."
              : isEditing
              ? "Update Result"
              : "Save Result"}
          </button>
        </div>
      </div>

      {/* All Results Card */}
      <div className="bg-white shadow-md rounded-lg p-6 border border-gray-200">
        <div className="flex items-center gap-2 mb-4">
          <i className="fas fa-table text-green-600 !text-[18px]" />
          <h2 className="font-semibold !text-[18px] text-gray-800">
            All Exam Results
          </h2>
        </div>

       {selectedClass && selectedExam ? (
  getFilteredResults().length > 0 ? (
    <div className="overflow-x-auto">
      <table className="min-w-full border border-gray-300 text-center !text-[14px] bg-white">
        <thead className="bg-gray-100 text-gray-800">
          <tr>
            <th className="border px-4 py-2">#</th>
            <th className="border px-4 py-2">Exam</th>
            <th className="border px-4 py-2">Class</th>
            <th className="border px-4 py-2">Section</th>
            <th className="border px-4 py-2">Subjects</th>
            <th className="border px-4 py-2">Actions</th>
          </tr>
        </thead>
        <tbody>
          {getFilteredResults().map((res, i) => (
            <tr key={res._id} className="border-b hover:bg-gray-50">
              <td className="border px-4 py-2">{i + 1}</td>
              <td className="border px-4 py-2">{res.examName}</td>
              <td className="border px-4 py-2">{res.className}</td>
              <td className="border px-4 py-2">{res.sectionName}</td>
              <td className="border px-4 py-2">
                {res.subjects.map((s) => s.subjectName).join(", ")}
              </td>
              <td className="border px-4 py-2">
                <div className="flex justify-center gap-2">
                  <button
                    onClick={() => handleEdit(res)}
                    className="bg-yellow-500 text-white px-3 py-1 rounded-lg !text-[13px]"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(res._id)}
                    className="bg-red-500 text-white px-3 py-1 rounded-lg !text-[13px]"
                  >
                    Delete
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : (
    <p className="text-gray-600 !text-[14px]">
      No results found for selected class & exam.
    </p>
  )
) : (
  <p className="text-gray-500 !text-[14px]">
    Select class and exam to view results.
  </p>
)}

      </div>
    </div>
  );
};

export default Result;
